import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useSessionUuid } from '#src/app/shared/hooks/session';
import type { ShareLink } from '#src/app/shared/hooks/share';

import { getContactDecisionMakerSuccessShareLinks } from './api';

const QUERY_KEY = 'contact-decision-maker-success-share-links';

/**
 * The app QueryClient sets `retry: false` with no refetch on focus or reconnect, and this key never
 * changes for the session — so without a retry of its own, one failed fetch would leave the post-send
 * sheet empty for good. Matches `usePetitionQRCode`.
 */
const MAX_RETRIES = 2;

/** Module-level so the "no links" case keeps one identity: `useShareActions` memoises on the array. */
const EMPTY_LINKS: readonly ShareLink[] = [];

export type ContactDecisionMakerSuccessShareLinksResult = {
	links: readonly ShareLink[];
	isLoading: boolean;
};

/**
 * Share links for the sheet shown after a supporter messages the decision maker. Kept out of every
 * page's prefetched pageData: the sheet is modal-gated, so most sessions never open it.
 *
 * Pass `enabled: false` until the sheet is about to open. Without it the query runs as soon as the
 * hook mounts, which for a modal-gated surface means fetching links nobody asked for.
 *
 * `staleTime: Infinity` is not about caching across mounts (the app QueryClient's
 * `refetchOnMount: 'always'` and `gcTime: 0` rule that out). It stops react-query refetching on every
 * `enabled` false → true flip: with the client's default `staleTime: 0` the data is stale at once, and a
 * failed send that returns to the review screen and is sent again would fetch the links a second time,
 * retries included. The key carries the session uuid, so nothing under it can change.
 */
export function useContactDecisionMakerSuccessShareLinks(
	petitionId: string,
	{ enabled = true }: { enabled?: boolean } = {},
): ContactDecisionMakerSuccessShareLinksResult {
	const utilityContext = useUtilityContext();
	const sessionUuid = useSessionUuid();

	const { data, isLoading } = useQuery({
		queryKey: [QUERY_KEY, { id: petitionId, sessionUuid }],
		queryFn: async () => getContactDecisionMakerSuccessShareLinks({ id: petitionId, utilityContext }),
		enabled,
		retry: MAX_RETRIES,
		staleTime: Infinity,
	});

	return { links: data ?? EMPTY_LINKS, isLoading: enabled && isLoading };
}
